import React, { useState, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
    Palette,
    FileText,
    BookOpen,
    FolderOpen,
    Folder,
    ChevronRight,
    Home,
    Download,
    Eye,
    Search,
    File,
    FileSpreadsheet,
    FileImage,
    FileArchive,
    Presentation,
    ArrowLeft,
    RefreshCw,
    ExternalLink,
    Copy,
    Check,
    List,
    Grid3X3,
    ArrowUpDown,
    X,
    Upload,
    FolderPlus,
    Trash2,
    Star,
    TrendingUp,
    Clock,
    FileUp,
    AlertCircle,
    Sparkles,
    Loader2
} from 'lucide-react';
import axiosInstance from '../utils/axiosConfig';
import Modal from './common/Modal';
import { useAuth } from '../context/AuthContext';

const API_URL = (process.env.REACT_APP_API_URL || '/api').replace(/\/$/, '');
const isOfficePreviewType = (tipo) => ['word', 'excel', 'powerpoint'].includes(tipo);
const isInlinePreviewType = (tipo) => ['pdf', 'imagen'].includes(tipo);
const buildRepoUrl = (endpoint, filePath) => {
    const token = localStorage.getItem('token');
    const params = new URLSearchParams({ path: filePath });
    if (token) params.set('token', token);
    return `${API_URL}/repositorio/${endpoint}?${params.toString()}`;
};

// Mapeo de iconos por nombre
const ICON_MAP = {
    'palette': Palette,
    'file-text': FileText,
    'book-open': BookOpen
};

// Colores para categorías
const COLOR_MAP = {
    'purple': {
        bg: 'bg-purple-50',
        bgHover: 'hover:bg-purple-100',
        border: 'border-purple-100',
        icon: 'text-purple-500',
        iconBg: 'bg-purple-100',
        accent: 'bg-purple-500'
    },
    'blue': {
        bg: 'bg-blue-50',
        bgHover: 'hover:bg-blue-100',
        border: 'border-blue-100',
        icon: 'text-blue-500',
        iconBg: 'bg-blue-100',
        accent: 'bg-blue-500'
    },
    'emerald': {
        bg: 'bg-emerald-50',
        bgHover: 'hover:bg-emerald-100',
        border: 'border-emerald-100',
        icon: 'text-emerald-500',
        iconBg: 'bg-emerald-100',
        accent: 'bg-emerald-500'
    },
    'gray': {
        bg: 'bg-gray-50',
        bgHover: 'hover:bg-gray-100',
        border: 'border-gray-100',
        icon: 'text-gray-500',
        iconBg: 'bg-gray-100',
        accent: 'bg-gray-500'
    }
};

// Iconos por tipo de archivo
const getFileIcon = (tipo, size = 24) => {
    switch (tipo) {
        case 'pdf':
            return <FileText size={size} className="text-red-500" />;
        case 'word':
            return <FileText size={size} className="text-blue-600" />;
        case 'excel':
            return <FileSpreadsheet size={size} className="text-green-600" />;
        case 'powerpoint':
            return <Presentation size={size} className="text-orange-500" />;
        case 'imagen':
            return <FileImage size={size} className="text-purple-500" />;
        case 'comprimido':
            return <FileArchive size={size} className="text-yellow-600" />;
        default:
            return <File size={size} className="text-gray-500" />;
    }
};

const DocumentsView = () => {
    const { user } = useAuth();
    const isAdmin = user?.role === 'admin' || user?.role === 'ti';
    const fileInputRef = useRef(null);

    const [view, setView] = useState('categorias');
    const [categorias, setCategorias] = useState([]);
    const [currentPath, setCurrentPath] = useState('');
    const [breadcrumb, setBreadcrumb] = useState([]);
    const [carpetas, setCarpetas] = useState([]);
    const [archivos, setArchivos] = useState([]);
    const [categoriaActual, setCategoriaActual] = useState(null);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [previewUrl, setPreviewUrl] = useState(null);
    const [previewFileName, setPreviewFileName] = useState('');

    const [globalSearch, setGlobalSearch] = useState('');
    const [searchResults, setSearchResults] = useState([]);
    const [searchLoading, setSearchLoading] = useState(false);
    const [displayMode, setDisplayMode] = useState('grid');
    const [sortBy, setSortBy] = useState('nombre');
    const [sortOrder, setSortOrder] = useState('asc');
    const [copiedLink, setCopiedLink] = useState(null);

    // Upload states
    const [isDragging, setIsDragging] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState([]);

    // Admin actions
    const [showNewFolderModal, setShowNewFolderModal] = useState(false);
    const [newFolderName, setNewFolderName] = useState('');
    const [deleteConfirm, setDeleteConfirm] = useState(null);

    // Favoritos
    const [favoritosMap, setFavoritosMap] = useState({});
    const [favoritosList, setFavoritosList] = useState([]);

    // Stats
    const [stats, setStats] = useState(null);

    // IA
    const [aiSearchMode, setAiSearchMode] = useState(false);
    const [aiSearchLoading, setAiSearchLoading] = useState(false);
    const [aiSearchResults, setAiSearchResults] = useState([]);
    const [summaryLoading, setSummaryLoading] = useState(false);
    const [summary, setSummary] = useState(null);

    // Cargar categorías
    const fetchCategorias = useCallback(async () => {
        try {
            setLoading(true);
            const response = await axiosInstance.get('/api/repositorio/categorias');
            setCategorias(response.data.categorias || []);
        } catch (error) {
            console.error('Error fetching categorias:', error);
        } finally {
            setLoading(false);
        }
    }, []);

    // Cargar stats
    const fetchStats = useCallback(async () => {
        try {
            const response = await axiosInstance.get('/api/repositorio/stats?limit=5');
            setStats(response.data);
        } catch (error) {
            console.error('Error fetching stats:', error);
        }
    }, []);

    // Navegar a carpeta
    const browsePath = useCallback(async (path) => {
        try {
            setLoading(true);
            const response = await axiosInstance.get(`/api/repositorio/browse?path=${encodeURIComponent(path)}`);
            const data = response.data;

            setCurrentPath(path);
            setBreadcrumb(data.breadcrumb || []);
            setCarpetas(data.carpetas || []);
            setArchivos(data.archivos || []);
            setCategoriaActual(data.categoria);
            setView('browse');

            // Cargar favoritos para archivos visibles
            if (data.archivos?.length > 0) {
                const paths = data.archivos.map(a => path ? `${path}/${a.nombre}` : a.nombre);
                checkFavoritos(paths);
            }
        } catch (error) {
            console.error('Error browsing path:', error);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchCategorias();
        fetchStats();
    }, [fetchCategorias, fetchStats]);

    // Check favoritos batch
    const checkFavoritos = async (paths) => {
        try {
            const response = await axiosInstance.post('/api/repositorio/favoritos/check', { paths });
            setFavoritosMap(prev => ({ ...prev, ...response.data.favoritos }));
        } catch (error) {
            // silently fail
        }
    };

    // Toggle favorito
    const toggleFavorito = async (archivo, path = null) => {
        const filePath = path || (currentPath ? `${currentPath}/${archivo.nombre}` : archivo.nombre);
        try {
            const response = await axiosInstance.post('/api/repositorio/favoritos/toggle', {
                path: filePath,
                nombre: archivo.nombre
            });
            setFavoritosMap(prev => ({ ...prev, [filePath]: response.data.favorito }));
        } catch (error) {
            console.error('Error toggling favorito:', error);
        }
    };

    // Cargar lista de favoritos
    const fetchFavoritos = async () => {
        try {
            const response = await axiosInstance.get('/api/repositorio/favoritos');
            setFavoritosList(response.data.favoritos || []);
        } catch (error) {
            console.error('Error fetching favoritos:', error);
        }
    };

    // Handlers
    const handleCategoryClick = (catId) => {
        browsePath(catId);
    };

    const handleFolderClick = (folderName) => {
        const newPath = currentPath ? `${currentPath}/${folderName}` : folderName;
        browsePath(newPath);
    };

    const handleBreadcrumbClick = (path) => {
        if (path === '') {
            setView('categorias');
            setCurrentPath('');
            setBreadcrumb([]);
        } else {
            browsePath(path);
        }
    };

    const handleDownload = async (archivo, customPath = null) => {
        const filePath = customPath || (currentPath ? `${currentPath}/${archivo.nombre}` : archivo.nombre);
        try {
            const response = await axiosInstance.get(`/api/repositorio/file?path=${encodeURIComponent(filePath)}`, {
                responseType: 'blob'
            });
            const url = window.URL.createObjectURL(response.data);
            const a = document.createElement('a');
            a.href = url;
            a.download = archivo.nombre || filePath.split('/').pop();
            document.body.appendChild(a);
            a.click();
            window.URL.revokeObjectURL(url);
            a.remove();
        } catch (error) {
            console.error('Error descargando archivo:', error);
        }
    };

    const handlePreview = (archivo, customPath = null) => {
        const filePath = customPath || (currentPath ? `${currentPath}/${archivo.nombre}` : archivo.nombre);

        if (isInlinePreviewType(archivo.tipo)) {
            setPreviewUrl(buildRepoUrl('preview', filePath));
            setPreviewFileName(archivo.nombre);
        } else if (isOfficePreviewType(archivo.tipo)) {
            setPreviewUrl(buildRepoUrl('preview-office', filePath));
            setPreviewFileName(archivo.nombre);
        }
    };

    const closePreview = () => {
        setPreviewUrl(null);
        setPreviewFileName('');
        setSummary(null);
    };

    // ─── IA ─────────────────────────────────────────────────────────────

    const handleAiSearch = async (query) => {
        if (!query || query.length < 3) return;
        try {
            setAiSearchLoading(true);
            setView('search');
            const response = await axiosInstance.post('/api/repositorio/ai/search', { query });
            setAiSearchResults(response.data.resultados || []);
            setSearchResults(response.data.resultados || []);
        } catch (error) {
            console.error('Error AI search:', error);
            setAiSearchResults([]);
            setSearchResults([]);
        } finally {
            setAiSearchLoading(false);
        }
    };

    const handleSummarize = async (filePath) => {
        try {
            setSummaryLoading(true);
            setSummary(null);
            const response = await axiosInstance.post('/api/repositorio/ai/summarize', { path: filePath });
            setSummary(response.data);
        } catch (error) {
            console.error('Error summarizing:', error);
            setSummary({ error: error.response?.data?.error || 'Error al generar resumen' });
        } finally {
            setSummaryLoading(false);
        }
    };

    // ─── Upload ─────────────────────────────────────────────────────────

    const handleFiles = async (fileList) => {
        if (!isAdmin || !fileList.length) return;

        setUploading(true);
        setUploadProgress(Array.from(fileList).map(f => ({ name: f.name, status: 'uploading' })));

        const formData = new FormData();
        formData.append('path', currentPath);
        Array.from(fileList).forEach(f => formData.append('files', f));

        try {
            const response = await axiosInstance.post('/api/repositorio/upload', formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });

            const data = response.data;
            setUploadProgress(
                Array.from(fileList).map(f => {
                    const err = data.errors?.find(e => e.startsWith(f.name));
                    return { name: f.name, status: err ? 'error' : 'done', error: err };
                })
            );

            // Refrescar vista
            setTimeout(() => {
                browsePath(currentPath);
                setUploadProgress([]);
                setUploading(false);
            }, 1500);
        } catch (error) {
            setUploadProgress(
                Array.from(fileList).map(f => ({ name: f.name, status: 'error', error: 'Error de conexión' }))
            );
            setTimeout(() => {
                setUploadProgress([]);
                setUploading(false);
            }, 3000);
        }
    };

    const handleDragOver = (e) => {
        e.preventDefault();
        if (isAdmin && view === 'browse') setIsDragging(true);
    };

    const handleDragLeave = (e) => {
        e.preventDefault();
        setIsDragging(false);
    };

    const handleDrop = (e) => {
        e.preventDefault();
        setIsDragging(false);
        if (isAdmin && view === 'browse') {
            handleFiles(e.dataTransfer.files);
        }
    };

    // ─── Crear carpeta ──────────────────────────────────────────────────

    const handleCreateFolder = async () => {
        if (!newFolderName.trim()) return;

        try {
            await axiosInstance.post('/api/repositorio/mkdir', {
                path: currentPath,
                nombre: newFolderName.trim()
            });
            setShowNewFolderModal(false);
            setNewFolderName('');
            browsePath(currentPath);
        } catch (error) {
            alert(error.response?.data?.error || 'Error al crear carpeta');
        }
    };

    // ─── Eliminar ───────────────────────────────────────────────────────

    const handleDelete = async (nombre, tipo) => {
        const filePath = currentPath ? `${currentPath}/${nombre}` : nombre;
        try {
            await axiosInstance.delete(`/api/repositorio/delete?path=${encodeURIComponent(filePath)}`);
            setDeleteConfirm(null);
            browsePath(currentPath);
        } catch (error) {
            alert(error.response?.data?.error || 'Error al eliminar');
        }
    };

    // ─── Búsqueda global ────────────────────────────────────────────────

    const handleGlobalSearch = async (query) => {
        if (query.length < 2) {
            setSearchResults([]);
            if (view === 'search') setView('categorias');
            return;
        }

        try {
            setSearchLoading(true);
            setView('search');
            const response = await axiosInstance.get(`/api/repositorio/search?q=${encodeURIComponent(query)}`);
            setSearchResults(response.data.resultados || []);
        } catch (error) {
            console.error('Error searching:', error);
            setSearchResults([]);
        } finally {
            setSearchLoading(false);
        }
    };

    useEffect(() => {
        if (aiSearchMode) return; // No auto-search en modo IA
        const timer = setTimeout(() => {
            if (globalSearch.length >= 2) {
                handleGlobalSearch(globalSearch);
            } else if (globalSearch.length === 0 && view === 'search') {
                setView('categorias');
            }
        }, 300);
        return () => clearTimeout(timer);
    }, [globalSearch]);

    // Copiar enlace directo
    const copyDirectLink = (archivo, path = null) => {
        const filePath = path || (currentPath ? `${currentPath}/${archivo.nombre}` : archivo.nombre);
        const endpoint = isOfficePreviewType(archivo.tipo) ? 'preview-office' : 'preview';
        const url = buildRepoUrl(endpoint, filePath);

        const copyToClipboard = async (text) => {
            try {
                if (navigator.clipboard && navigator.clipboard.writeText) {
                    await navigator.clipboard.writeText(text);
                } else {
                    const textArea = document.createElement('textarea');
                    textArea.value = text;
                    textArea.style.position = 'fixed';
                    textArea.style.left = '-999999px';
                    document.body.appendChild(textArea);
                    textArea.focus();
                    textArea.select();
                    document.execCommand('copy');
                    textArea.remove();
                }
                setCopiedLink(archivo.nombre);
                setTimeout(() => setCopiedLink(null), 2000);
            } catch (err) {
                console.error('Error al copiar:', err);
            }
        };

        copyToClipboard(url);
    };

    // Ordenamiento
    const sortFiles = (files) => {
        return [...files].sort((a, b) => {
            let comparison = 0;
            switch (sortBy) {
                case 'nombre': comparison = a.nombre.localeCompare(b.nombre); break;
                case 'fecha': comparison = new Date(a.modificado) - new Date(b.modificado); break;
                case 'tamaño': comparison = a.tamaño - b.tamaño; break;
                case 'tipo': comparison = a.tipo.localeCompare(b.tipo); break;
                default: comparison = 0;
            }
            return sortOrder === 'asc' ? comparison : -comparison;
        });
    };

    const toggleSort = (field) => {
        if (sortBy === field) {
            setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
        } else {
            setSortBy(field);
            setSortOrder('asc');
        }
    };

    const filteredArchivos = sortFiles(
        archivos.filter(a => a.nombre.toLowerCase().includes(searchTerm.toLowerCase()))
    );
    const filteredCarpetas = carpetas.filter(c =>
        c.nombre.toLowerCase().includes(searchTerm.toLowerCase())
    );

    // ─── Render: Stats cards ────────────────────────────────────────────

    const renderStats = () => {
        if (!stats) return null;
        const { mas_descargados, mas_recientes } = stats;
        if (!mas_descargados?.length && !mas_recientes?.length) return null;

        return (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
                {/* Más descargados */}
                {mas_descargados?.length > 0 && (
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="bg-white rounded-2xl border border-gray-100 p-5"
                    >
                        <div className="flex items-center gap-2 mb-4">
                            <TrendingUp size={18} className="text-orange-500" />
                            <h3 className="font-semibold text-gray-900">Más descargados</h3>
                        </div>
                        <div className="space-y-2">
                            {mas_descargados.map((item, i) => (
                                <div key={i} className="flex items-center gap-3 py-1.5">
                                    <span className="text-xs font-bold text-gray-300 w-4">{i + 1}</span>
                                    {getFileIcon(item.archivo_tipo, 16)}
                                    <span className="flex-1 text-sm text-gray-700 truncate">{item.archivo_nombre}</span>
                                    <span className="text-xs text-gray-400">{item.descargas} descargas</span>
                                </div>
                            ))}
                        </div>
                    </motion.div>
                )}

                {/* Recientes */}
                {mas_recientes?.length > 0 && (
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.05 }}
                        className="bg-white rounded-2xl border border-gray-100 p-5"
                    >
                        <div className="flex items-center gap-2 mb-4">
                            <Clock size={18} className="text-blue-500" />
                            <h3 className="font-semibold text-gray-900">Subidos recientemente</h3>
                        </div>
                        <div className="space-y-2">
                            {mas_recientes.map((item, i) => (
                                <div key={i} className="flex items-center gap-3 py-1.5">
                                    {getFileIcon(item.archivo_tipo, 16)}
                                    <span className="flex-1 text-sm text-gray-700 truncate">{item.archivo_nombre}</span>
                                    <span className="text-xs text-gray-400">
                                        {new Date(item.created_at).toLocaleDateString()}
                                    </span>
                                </div>
                            ))}
                        </div>
                    </motion.div>
                )}
            </div>
        );
    };

    // ─── Render: Categorías ─────────────────────────────────────────────

    const renderCategorias = () => (
        <>
            {renderStats()}

            {categorias.length === 0 ? (
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="text-center py-24 bg-white rounded-3xl border-2 border-dashed border-gray-200"
                >
                    <div className="w-20 h-20 bg-gray-50 rounded-full flex items-center justify-center mx-auto mb-5">
                        <FolderOpen size={36} className="text-gray-300" />
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">Repositorio vacío</h3>
                    <p className="text-gray-500 max-w-md mx-auto">
                        No hay categorías de documentos configuradas. Las carpetas dentro de <code className="text-sm bg-gray-100 px-2 py-0.5 rounded">uploads/repositorio/</code> aparecerán aquí automáticamente.
                    </p>
                </motion.div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {categorias.map((cat, index) => {
                        const IconComponent = ICON_MAP[cat.icono] || FileText;
                        const colors = COLOR_MAP[cat.color] || COLOR_MAP.blue;

                        return (
                            <motion.button
                                type="button"
                                key={cat.id}
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: index * 0.03 }}
                                onClick={() => handleCategoryClick(cat.id)}
                                className={`
                                    ${colors.bg} ${colors.bgHover} ${colors.border}
                                    border-2 rounded-3xl p-8 cursor-pointer
                                    transition-all duration-300 group
                                    hover:shadow-xl hover:-translate-y-1
                                    text-left w-full focus:outline-none focus:ring-2 focus:ring-blue-500/30
                                `}
                                aria-label={`Abrir categoría ${cat.nombre}`}
                            >
                                <div className={`
                                    w-16 h-16 ${colors.iconBg} rounded-2xl
                                    flex items-center justify-center mb-6
                                    group-hover:scale-110 transition-transform duration-300
                                `}>
                                    <IconComponent size={32} className={colors.icon} />
                                </div>

                                <h3 className="text-2xl font-bold text-gray-900 mb-2">
                                    {cat.nombre}
                                </h3>

                                <p className="text-gray-600 text-sm mb-6 line-clamp-2">
                                    {cat.descripcion}
                                </p>

                                <div className="flex items-center justify-between">
                                    <span className="text-sm font-medium text-gray-500">
                                        {cat.archivos_count} {cat.archivos_count === 1 ? 'archivo' : 'archivos'}
                                    </span>
                                    <motion.div
                                        className={`${colors.accent} text-white p-2 rounded-xl`}
                                        whileHover={{ scale: 1.1 }}
                                        whileTap={{ scale: 0.95 }}
                                    >
                                        <ChevronRight size={20} />
                                    </motion.div>
                                </div>
                            </motion.button>
                        );
                    })}
                </div>
            )}
        </>
    );

    // ─── Render: Acciones de archivo (reutilizable) ─────────────────────

    const renderFileActions = (archivo, filePath, isListView = false) => {
        const isFav = favoritosMap[filePath];
        const sizeClass = isListView ? 16 : 18;
        const btnClass = isListView
            ? 'p-2 rounded-lg transition-colors'
            : 'p-2.5 rounded-xl transition-colors';

        return (
            <div className={`flex items-center gap-1 ${isListView ? 'sm:opacity-0 sm:group-hover:opacity-100 sm:group-focus-within:opacity-100 transition-opacity' : ''}`}>
                {/* Favorito */}
                <motion.button
                    onClick={(e) => { e.stopPropagation(); toggleFavorito(archivo); }}
                    className={`${btnClass} ${isFav ? 'text-yellow-500' : 'text-gray-400 hover:text-yellow-500'}`}
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                    title={isFav ? 'Quitar de favoritos' : 'Agregar a favoritos'}
                    aria-label={isFav ? 'Quitar de favoritos' : 'Agregar a favoritos'}
                >
                    <Star size={sizeClass} fill={isFav ? 'currentColor' : 'none'} />
                </motion.button>

                {/* Preview */}
                {['pdf', 'imagen', 'word', 'excel', 'powerpoint'].includes(archivo.tipo) && (
                    <motion.button
                        onClick={(e) => { e.stopPropagation(); handlePreview(archivo); }}
                        className={`${btnClass} bg-blue-500 text-white hover:bg-blue-600`}
                        whileHover={{ scale: 1.1 }}
                        whileTap={{ scale: 0.9 }}
                        title="Vista previa"
                        aria-label="Vista previa"
                    >
                        <Eye size={sizeClass} />
                    </motion.button>
                )}

                {/* Copiar enlace */}
                <motion.button
                    onClick={(e) => { e.stopPropagation(); copyDirectLink(archivo); }}
                    className={`${btnClass} ${copiedLink === archivo.nombre ? 'bg-green-500 text-white' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`}
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                    title="Copiar enlace"
                    aria-label="Copiar enlace"
                >
                    {copiedLink === archivo.nombre ? <Check size={sizeClass} /> : <Copy size={sizeClass} />}
                </motion.button>

                {/* Descargar */}
                <motion.button
                    onClick={(e) => { e.stopPropagation(); handleDownload(archivo); }}
                    className={`${btnClass} bg-gray-900 text-white hover:bg-gray-800`}
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                    title="Descargar"
                    aria-label="Descargar"
                >
                    <Download size={sizeClass} />
                </motion.button>

                {/* Eliminar (solo admin) */}
                {isAdmin && (
                    <motion.button
                        onClick={(e) => { e.stopPropagation(); setDeleteConfirm({ nombre: archivo.nombre, tipo: 'archivo' }); }}
                        className={`${btnClass} text-red-400 hover:text-red-600 hover:bg-red-50`}
                        whileHover={{ scale: 1.1 }}
                        whileTap={{ scale: 0.9 }}
                        title="Eliminar"
                        aria-label="Eliminar"
                    >
                        <Trash2 size={sizeClass} />
                    </motion.button>
                )}
            </div>
        );
    };

    // ─── Render: Browser ────────────────────────────────────────────────

    const renderBrowser = () => (
        <div
            className="space-y-6"
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
        >
            {/* Breadcrumb */}
            <div className="flex items-center gap-2 text-sm bg-gray-50 rounded-xl p-3">
                <motion.button
                    onClick={() => handleBreadcrumbClick('')}
                    className="flex items-center gap-1 text-gray-500 hover:text-gray-900 transition-colors"
                    whileHover={{ scale: 1.05 }}
                >
                    <Home size={16} />
                    <span>Documentos</span>
                </motion.button>

                {breadcrumb.map((item, index) => (
                    <React.Fragment key={item.path}>
                        <ChevronRight size={14} className="text-gray-400" />
                        <motion.button
                            onClick={() => handleBreadcrumbClick(item.path)}
                            className={`
                                ${index === breadcrumb.length - 1
                                    ? 'text-gray-900 font-semibold'
                                    : 'text-gray-500 hover:text-gray-900'
                                }
                                transition-colors
                            `}
                            whileHover={{ scale: 1.02 }}
                        >
                            {item.nombre}
                        </motion.button>
                    </React.Fragment>
                ))}
            </div>

            {/* Barra de búsqueda + acciones admin */}
            <div className="flex gap-3">
                <div className="relative flex-1">
                    <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
                    <input
                        type="text"
                        placeholder="Buscar en esta carpeta..."
                        className="w-full pl-12 pr-4 py-3 rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                </div>

                {isAdmin && (
                    <div className="flex items-center gap-2">
                        <motion.button
                            onClick={() => fileInputRef.current?.click()}
                            className="flex items-center gap-2 px-4 py-2.5 bg-blue-500 text-white rounded-xl hover:bg-blue-600 transition-colors text-sm font-medium"
                            whileHover={{ scale: 1.02 }}
                            whileTap={{ scale: 0.98 }}
                            disabled={uploading}
                        >
                            <Upload size={18} />
                            <span className="hidden sm:inline">Subir</span>
                        </motion.button>
                        <motion.button
                            onClick={() => setShowNewFolderModal(true)}
                            className="flex items-center gap-2 px-4 py-2.5 bg-gray-100 text-gray-700 rounded-xl hover:bg-gray-200 transition-colors text-sm font-medium"
                            whileHover={{ scale: 1.02 }}
                            whileTap={{ scale: 0.98 }}
                        >
                            <FolderPlus size={18} />
                            <span className="hidden sm:inline">Carpeta</span>
                        </motion.button>
                        <input
                            ref={fileInputRef}
                            type="file"
                            multiple
                            className="hidden"
                            onChange={(e) => handleFiles(e.target.files)}
                        />
                    </div>
                )}
            </div>

            {/* Drop zone overlay */}
            <AnimatePresence>
                {isDragging && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 z-50 bg-blue-500/10 backdrop-blur-sm flex items-center justify-center"
                        onDragOver={handleDragOver}
                        onDragLeave={handleDragLeave}
                        onDrop={handleDrop}
                    >
                        <motion.div
                            initial={{ scale: 0.9 }}
                            animate={{ scale: 1 }}
                            className="bg-white rounded-3xl border-2 border-dashed border-blue-400 p-16 text-center shadow-2xl"
                        >
                            <FileUp size={48} className="mx-auto text-blue-500 mb-4" />
                            <h3 className="text-xl font-bold text-gray-900 mb-2">Suelta los archivos aquí</h3>
                            <p className="text-gray-500">Se subirán a <code className="bg-gray-100 px-2 py-0.5 rounded text-sm">{currentPath || 'repositorio'}</code></p>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Upload progress */}
            <AnimatePresence>
                {uploadProgress.length > 0 && (
                    <motion.div
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                        className="bg-white border border-gray-200 rounded-xl p-4 space-y-2"
                    >
                        {uploadProgress.map((item, i) => (
                            <div key={i} className="flex items-center gap-3">
                                {item.status === 'uploading' ? (
                                    <RefreshCw size={16} className="text-blue-500 animate-spin" />
                                ) : item.status === 'done' ? (
                                    <Check size={16} className="text-green-500" />
                                ) : (
                                    <AlertCircle size={16} className="text-red-500" />
                                )}
                                <span className="text-sm text-gray-700 flex-1 truncate">{item.name}</span>
                                {item.error && <span className="text-xs text-red-500">{item.error}</span>}
                            </div>
                        ))}
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Contenido */}
            {displayMode === 'grid' ? (
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4">
                    <AnimatePresence>
                        {filteredCarpetas.map((carpeta, index) => (
	                            <motion.div
	                                key={`folder-${carpeta.nombre}`}
                                initial={{ opacity: 0, scale: 0.9 }}
                                animate={{ opacity: 1, scale: 1 }}
                                exit={{ opacity: 0, scale: 0.9 }}
	                                transition={{ delay: index * 0.03 }}
	                                onClick={() => handleFolderClick(carpeta.nombre)}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter' || e.key === ' ') {
                                            e.preventDefault();
                                            handleFolderClick(carpeta.nombre);
                                        }
                                    }}
	                                className="bg-white border border-gray-100 rounded-2xl p-4 cursor-pointer hover:shadow-lg hover:border-blue-200 transition-all duration-200 group relative text-left focus:outline-none focus:ring-2 focus:ring-blue-500/30"
                                    role="button"
                                    tabIndex={0}
                                    aria-label={`Abrir carpeta ${carpeta.nombre}`}
	                            >
                                <div className="flex flex-col items-center text-center">
                                    <div className="mb-3 p-3 bg-amber-50 rounded-xl group-hover:bg-amber-100 transition-colors">
                                        <FolderOpen size={32} className="text-amber-500" />
                                    </div>
                                    <span className="text-sm font-medium text-gray-700 line-clamp-2">
                                        {carpeta.nombre}
                                    </span>
                                    <span className="text-xs text-gray-400 mt-1">
                                        {carpeta.archivos_count} archivos
                                    </span>
                                </div>

                                {/* Eliminar carpeta (admin) */}
                                {isAdmin && carpeta.archivos_count === 0 && (
                                    <motion.button
                                        onClick={(e) => { e.stopPropagation(); setDeleteConfirm({ nombre: carpeta.nombre, tipo: 'carpeta' }); }}
                                        className="absolute top-2 right-2 p-1.5 rounded-lg text-gray-300 hover:text-red-500 hover:bg-red-50 opacity-0 group-hover:opacity-100 transition-all"
                                        whileHover={{ scale: 1.1 }}
                                    >
                                        <Trash2 size={14} />
                                    </motion.button>
                                )}
	                            </motion.div>
                        ))}
                    </AnimatePresence>

                    <AnimatePresence>
                        {filteredArchivos.map((archivo, index) => {
                            const filePath = currentPath ? `${currentPath}/${archivo.nombre}` : archivo.nombre;
                            return (
                                <motion.div
                                    key={`file-${archivo.nombre}`}
                                    initial={{ opacity: 0, scale: 0.9 }}
                                    animate={{ opacity: 1, scale: 1 }}
                                    exit={{ opacity: 0, scale: 0.9 }}
                                    transition={{ delay: (filteredCarpetas.length + index) * 0.03 }}
                                    className="bg-white border border-gray-100 rounded-2xl p-4 hover:shadow-lg hover:border-gray-200 transition-all duration-200 group relative"
                                >
                                    <div className="flex flex-col items-center text-center">
                                        {archivo.tipo === 'imagen' ? (
                                            <div className="mb-3 w-full aspect-square rounded-xl overflow-hidden bg-gray-50 flex items-center justify-center">
                                                <img
                                                    src={buildRepoUrl('preview', filePath)}
                                                    alt={archivo.nombre}
                                                    className="w-full h-full object-contain group-hover:scale-105 transition-transform duration-300"
                                                    loading="lazy"
                                                    onError={(e) => {
                                                        e.target.style.display = 'none';
                                                    }}
                                                />
                                            </div>
                                        ) : (
                                            <div className="mb-3 p-3 bg-gray-50 rounded-xl group-hover:bg-gray-100 transition-colors">
                                                {getFileIcon(archivo.tipo)}
                                            </div>
                                        )}
                                        <span className="text-sm font-medium text-gray-700 line-clamp-2 mb-1">
                                            {archivo.nombre}
                                        </span>
                                        <span className="text-xs text-gray-400 uppercase">
                                            {archivo.extension} • {archivo.tamaño_formateado}
                                        </span>
                                    </div>

                                    {/* Fav indicator */}
                                    {favoritosMap[filePath] && (
                                        <Star size={12} className="absolute top-3 right-3 text-yellow-500" fill="currentColor" />
                                    )}

                                    {/* Overlay de acciones */}
	                                    <div className="absolute inset-0 bg-white/90 rounded-2xl sm:opacity-0 sm:group-hover:opacity-100 sm:group-focus-within:opacity-100 transition-opacity duration-200 flex items-center justify-center gap-1">
                                        {renderFileActions(archivo, filePath)}
                                    </div>
                                </motion.div>
                            );
                        })}
                    </AnimatePresence>
                </div>
            ) : (
                /* Vista Lista */
                <div className="space-y-2">
                    {filteredCarpetas.map((carpeta, index) => (
	                        <motion.div
	                            key={`folder-list-${carpeta.nombre}`}
                            initial={{ opacity: 0, x: -10 }}
                            animate={{ opacity: 1, x: 0 }}
	                            transition={{ delay: index * 0.02 }}
	                            onClick={() => handleFolderClick(carpeta.nombre)}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter' || e.key === ' ') {
                                        e.preventDefault();
                                        handleFolderClick(carpeta.nombre);
                                    }
                                }}
	                            className="bg-white rounded-xl border border-gray-100 p-4 hover:shadow-md hover:border-blue-200 transition-all flex items-center gap-4 cursor-pointer group text-left w-full focus:outline-none focus:ring-2 focus:ring-blue-500/30"
                                role="button"
                                tabIndex={0}
                                aria-label={`Abrir carpeta ${carpeta.nombre}`}
	                        >
                            <div className="p-2 bg-amber-50 rounded-lg group-hover:bg-amber-100 transition-colors">
                                <FolderOpen size={24} className="text-amber-500" />
                            </div>
                            <div className="flex-1 min-w-0">
                                <p className="font-medium text-gray-900 truncate">{carpeta.nombre}</p>
                                <p className="text-sm text-gray-500">{carpeta.archivos_count} archivos</p>
                            </div>
                            {isAdmin && carpeta.archivos_count === 0 && (
                                <motion.button
                                    onClick={(e) => { e.stopPropagation(); setDeleteConfirm({ nombre: carpeta.nombre, tipo: 'carpeta' }); }}
                                    className="p-2 rounded-lg text-gray-300 hover:text-red-500 hover:bg-red-50 opacity-0 group-hover:opacity-100 transition-all"
                                    whileHover={{ scale: 1.1 }}
                                >
                                    <Trash2 size={16} />
                                </motion.button>
                            )}
                            <ChevronRight size={20} className="text-gray-400" />
	                        </motion.div>
                    ))}

                    {filteredArchivos.map((archivo, index) => {
                        const filePath = currentPath ? `${currentPath}/${archivo.nombre}` : archivo.nombre;
                        return (
                            <motion.div
                                key={`file-list-${archivo.nombre}`}
                                initial={{ opacity: 0, x: -10 }}
                                animate={{ opacity: 1, x: 0 }}
                                transition={{ delay: (filteredCarpetas.length + index) * 0.02 }}
                                className="bg-white rounded-xl border border-gray-100 p-4 hover:shadow-md hover:border-gray-200 transition-all flex items-center gap-4 group"
                            >
                                <div className="p-2 bg-gray-50 rounded-lg">
                                    {getFileIcon(archivo.tipo)}
                                </div>
                                <div className="flex-1 min-w-0">
                                    <p className="font-medium text-gray-900 truncate">{archivo.nombre}</p>
                                    <p className="text-sm text-gray-500">{archivo.tamaño_formateado} • {new Date(archivo.modificado).toLocaleDateString()}</p>
                                </div>
                                {favoritosMap[filePath] && (
                                    <Star size={14} className="text-yellow-500" fill="currentColor" />
                                )}
                                <span className="text-xs text-gray-400 uppercase px-2 py-1 bg-gray-100 rounded-lg">{archivo.extension}</span>
                                {renderFileActions(archivo, filePath, true)}
                            </motion.div>
                        );
                    })}
                </div>
            )}

            {/* Estado vacío */}
            {filteredCarpetas.length === 0 && filteredArchivos.length === 0 && !loading && (
                <div className="text-center py-20 bg-white rounded-3xl border border-gray-100 border-dashed">
                    <div className="w-20 h-20 bg-gray-50 rounded-full flex items-center justify-center mx-auto mb-4">
                        <Folder size={32} className="text-gray-300" />
                    </div>
                    <h3 className="text-gray-900 font-bold text-lg">Carpeta vacía</h3>
                    <p className="text-gray-500 mb-4">No hay archivos en esta ubicación</p>
                    {isAdmin && (
                        <motion.button
                            onClick={() => fileInputRef.current?.click()}
                            className="inline-flex items-center gap-2 px-5 py-2.5 bg-blue-500 text-white rounded-xl hover:bg-blue-600 transition-colors text-sm font-medium"
                            whileHover={{ scale: 1.02 }}
                        >
                            <Upload size={18} />
                            Subir archivos
                        </motion.button>
                    )}
                </div>
            )}
        </div>
    );

    // ─── Render: Favoritos panel ────────────────────────────────────────

    const renderFavoritos = () => (
        <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-4"
        >
            <div className="flex items-center justify-between">
                <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                    <Star size={20} className="text-yellow-500" fill="currentColor" />
                    Mis favoritos
                </h2>
            </div>

            {favoritosList.length === 0 ? (
                <div className="text-center py-16 bg-white rounded-3xl border border-gray-100">
                    <Star size={40} className="mx-auto text-gray-200 mb-4" />
                    <h3 className="text-gray-900 font-bold text-lg">Sin favoritos</h3>
                    <p className="text-gray-500">Marca archivos con la estrella para acceder rápido</p>
                </div>
            ) : (
                <div className="space-y-2">
                    {favoritosList.map((fav, index) => (
                        <motion.div
                            key={fav.id}
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: index * 0.03 }}
                            className={`bg-white rounded-xl border border-gray-100 p-4 hover:shadow-md transition-all flex items-center gap-4 group ${!fav.existe ? 'opacity-50' : ''}`}
                        >
                            <div className="p-2 bg-gray-50 rounded-lg">
                                {getFileIcon(fav.tipo || 'otro')}
                            </div>
                            <div className="flex-1 min-w-0">
                                <p className="font-medium text-gray-900 truncate">{fav.archivo_nombre}</p>
                                <p className="text-sm text-gray-500 truncate">{fav.archivo_path}</p>
                            </div>
                            {!fav.existe && (
                                <span className="text-xs text-red-400 bg-red-50 px-2 py-1 rounded">eliminado</span>
                            )}
                            {fav.existe && (
                                <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                    <motion.button
                                        onClick={() => {
                                            const endpoint = isOfficePreviewType(fav.tipo) ? 'preview-office' : 'preview';
                                            setPreviewUrl(buildRepoUrl(endpoint, fav.archivo_path));
                                            setPreviewFileName(fav.archivo_nombre);
                                        }}
                                        className="p-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
                                        whileHover={{ scale: 1.05 }}
                                        title="Vista previa"
                                    >
                                        <Eye size={16} />
                                    </motion.button>
                                    <motion.button
                                        onClick={() => handleDownload({ nombre: fav.archivo_nombre }, fav.archivo_path)}
                                        className="p-2 bg-gray-900 text-white rounded-lg hover:bg-gray-800 transition-colors"
                                        whileHover={{ scale: 1.05 }}
                                        title="Descargar"
                                    >
                                        <Download size={16} />
                                    </motion.button>
                                </div>
                            )}
                            <motion.button
                                onClick={() => {
                                    toggleFavorito({ nombre: fav.archivo_nombre }, fav.archivo_path);
                                    setFavoritosList(prev => prev.filter(f => f.id !== fav.id));
                                }}
                                className="p-2 text-yellow-500 hover:text-yellow-600"
                                whileHover={{ scale: 1.1 }}
                                title="Quitar de favoritos"
                            >
                                <Star size={16} fill="currentColor" />
                            </motion.button>
                        </motion.div>
                    ))}
                </div>
            )}
        </motion.div>
    );

    // ─── Render: Búsqueda ───────────────────────────────────────────────

    const renderSearch = () => {
        const isAiResult = aiSearchResults.length > 0 && aiSearchMode;
        const isLoading = searchLoading || aiSearchLoading;

        return (
        <div className="space-y-4">
            <div className="flex items-center justify-between">
                <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                    {isLoading ? (
                        <><Loader2 size={18} className="animate-spin text-purple-500" /> Buscando{aiSearchMode ? ' con IA' : ''}...</>
                    ) : (
                        <>
                            {isAiResult && <Sparkles size={18} className="text-purple-500" />}
                            {searchResults.length} resultados para "{globalSearch}"
                        </>
                    )}
                </h2>
            </div>

            {isLoading ? (
                <div className="grid grid-cols-1 gap-4">
                    {[1, 2, 3].map(i => (
                        <div key={i} className="h-20 bg-gray-100 rounded-xl skeleton" />
                    ))}
                </div>
            ) : searchResults.length === 0 ? (
                <div className="text-center py-20 bg-white rounded-3xl border border-gray-100">
                    <Search size={48} className="mx-auto text-gray-300 mb-4" />
                    <h3 className="text-gray-900 font-bold text-lg">No se encontraron resultados</h3>
                    <p className="text-gray-500">Intenta con otro término de búsqueda</p>
                </div>
            ) : (
                <div className="space-y-2">
                    {searchResults.map((archivo, index) => (
                        <motion.div
                            key={`search-${index}`}
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: index * 0.03 }}
                            className="bg-white rounded-xl border border-gray-100 p-4 hover:shadow-md hover:border-gray-200 transition-all flex items-center gap-4 group"
                        >
                            <div className="p-2 bg-gray-50 rounded-lg">
                                {getFileIcon(archivo.tipo)}
                            </div>
                            <div className="flex-1 min-w-0">
                                <p className="font-medium text-gray-900 truncate">{archivo.nombre}</p>
                                <div className="flex items-center gap-2">
                                    <p className="text-sm text-gray-500 truncate">{archivo.carpeta}</p>
                                    {archivo.razon && (
                                        <span className="text-xs text-purple-600 bg-purple-50 px-2 py-0.5 rounded-full truncate max-w-[200px]" title={archivo.razon}>
                                            {archivo.razon}
                                        </span>
                                    )}
                                </div>
                            </div>
                            {archivo.relevancia && (
                                <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${archivo.relevancia === 'alta' ? 'bg-green-50 text-green-600' : 'bg-yellow-50 text-yellow-600'}`}>
                                    {archivo.relevancia}
                                </span>
                            )}
                            <span className="text-xs text-gray-400 uppercase">{archivo.extension}</span>
                            <span className="text-sm text-gray-500">{archivo.tamaño_formateado}</span>

                            <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                {['pdf', 'imagen', 'word', 'excel', 'powerpoint'].includes(archivo.tipo) && (
                                    <motion.button
                                        onClick={() => {
                                            handlePreview(archivo, archivo.ruta);
                                        }}
                                        className="p-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
                                        whileHover={{ scale: 1.05 }}
                                        whileTap={{ scale: 0.95 }}
                                        title="Vista previa"
                                    >
                                        <Eye size={16} />
                                    </motion.button>
                                )}
                                <motion.button
                                    onClick={() => copyDirectLink(archivo, archivo.ruta)}
                                    className={`p-2 rounded-lg transition-colors ${copiedLink === archivo.nombre ? 'bg-green-500 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
                                    whileHover={{ scale: 1.05 }}
                                    whileTap={{ scale: 0.95 }}
                                    title="Copiar enlace"
                                >
                                    {copiedLink === archivo.nombre ? <Check size={16} /> : <Copy size={16} />}
                                </motion.button>
                                <motion.button
                                    onClick={() => handleDownload(archivo, archivo.ruta)}
                                    className="p-2 bg-gray-900 text-white rounded-lg hover:bg-gray-800 transition-colors"
                                    whileHover={{ scale: 1.05 }}
                                    whileTap={{ scale: 0.95 }}
                                    title="Descargar"
                                >
                                    <Download size={16} />
                                </motion.button>
                            </div>
                        </motion.div>
                    ))}
                </div>
            )}
        </div>
    );
    };

    // ─── Render: Preview Modal ──────────────────────────────────────────

    const renderPreviewModal = () => (
        <Modal
            isOpen={!!previewUrl}
            onClose={closePreview}
            size="3xl"
            closeOnOverlay={true}
            closeOnEscape={true}
        >
            <Modal.Header onClose={closePreview}>
                <div className="flex items-center gap-3">
                    <Eye size={20} className="text-gray-500" />
                    <span className="truncate max-w-md">{previewFileName || 'Vista Previa'}</span>
                </div>
            </Modal.Header>
            <Modal.Body className="p-0 bg-gray-100">
                {previewUrl && (
                    previewFileName && /\.(png|jpg|jpeg|gif|svg|webp)$/i.test(previewFileName) ? (
                        <div className="flex items-center justify-center p-4" style={{ height: '75vh' }}>
                            <img
                                src={previewUrl}
                                alt={previewFileName}
                                className="max-w-full max-h-full object-contain rounded-lg shadow-lg"
                            />
                        </div>
                    ) : (
                        <iframe
                            src={previewUrl}
                            className="w-full border-0 bg-white"
                            title="Vista previa del documento"
                            style={{ height: '75vh' }}
                        />
                    )
                )}
            </Modal.Body>

            {/* Resumen IA */}
            {summary && !summary.error && (
                <div className="px-6 py-4 border-t border-gray-100 bg-purple-50/50">
                    <div className="flex items-center gap-2 mb-3">
                        <Sparkles size={16} className="text-purple-500" />
                        <span className="text-sm font-semibold text-purple-700">Resumen IA</span>
                        {summary.tipo_documento && (
                            <span className="text-xs bg-purple-100 text-purple-600 px-2 py-0.5 rounded-full">{summary.tipo_documento}</span>
                        )}
                    </div>
                    {summary.titulo && <h4 className="font-semibold text-gray-900 text-sm mb-1">{summary.titulo}</h4>}
                    <p className="text-sm text-gray-700 mb-2">{summary.resumen}</p>
                    {summary.puntos_clave?.length > 0 && (
                        <ul className="text-sm text-gray-600 space-y-0.5 mb-2">
                            {summary.puntos_clave.map((p, i) => (
                                <li key={i} className="flex items-start gap-1.5">
                                    <span className="text-purple-400 mt-1">-</span>
                                    <span>{p}</span>
                                </li>
                            ))}
                        </ul>
                    )}
                    {summary.palabras_clave?.length > 0 && (
                        <div className="flex flex-wrap gap-1">
                            {summary.palabras_clave.map((kw, i) => (
                                <span key={i} className="text-xs bg-white text-gray-500 px-2 py-0.5 rounded-full border border-gray-200">{kw}</span>
                            ))}
                        </div>
                    )}
                </div>
            )}
            {summary?.error && (
                <div className="px-6 py-3 border-t border-gray-100 bg-red-50">
                    <p className="text-sm text-red-600">{summary.error}</p>
                </div>
            )}

            <Modal.Footer>
                <div className="flex items-center justify-between w-full">
                    <div className="flex items-center gap-2">
                        {/* Botón resumir */}
                        {previewFileName && /\.(pdf|docx?|xlsx?|pptx?|txt)$/i.test(previewFileName) && (
                            <motion.button
                                onClick={() => {
                                    const path = previewUrl?.includes('path=')
                                        ? decodeURIComponent(previewUrl.split('path=')[1])
                                        : '';
                                    if (path) handleSummarize(path);
                                }}
                                disabled={summaryLoading}
                                className="flex items-center gap-2 px-4 py-2 bg-purple-500 hover:bg-purple-600 text-white rounded-lg transition-colors text-sm font-medium disabled:opacity-50"
                                whileHover={{ scale: 1.02 }}
                                whileTap={{ scale: 0.98 }}
                            >
                                {summaryLoading ? (
                                    <Loader2 size={16} className="animate-spin" />
                                ) : (
                                    <Sparkles size={16} />
                                )}
                                {summaryLoading ? 'Analizando...' : summary ? 'Regenerar resumen' : 'Resumir con IA'}
                            </motion.button>
                        )}
                    </div>
                    <div className="flex items-center gap-2">
                        <a
                            href={previewUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition-colors text-sm font-medium"
                        >
                            <ExternalLink size={16} />
                            Abrir en nueva pestaña
                        </a>
                        <button
                            onClick={closePreview}
                            className="px-4 py-2 bg-gray-900 hover:bg-gray-800 text-white rounded-lg transition-colors text-sm font-medium"
                        >
                            Cerrar
                        </button>
                    </div>
                </div>
            </Modal.Footer>
        </Modal>
    );

    // ─── Render: Modales de acción ──────────────────────────────────────

    const renderNewFolderModal = () => (
        <Modal
            isOpen={showNewFolderModal}
            onClose={() => { setShowNewFolderModal(false); setNewFolderName(''); }}
            size="sm"
            closeOnOverlay={true}
            closeOnEscape={true}
        >
            <Modal.Header onClose={() => { setShowNewFolderModal(false); setNewFolderName(''); }}>
                <div className="flex items-center gap-3">
                    <FolderPlus size={20} className="text-gray-500" />
                    <span>Nueva carpeta</span>
                </div>
            </Modal.Header>
            <Modal.Body>
                <input
                    type="text"
                    placeholder="Nombre de la carpeta..."
                    className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all"
                    value={newFolderName}
                    onChange={(e) => setNewFolderName(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleCreateFolder()}
                    autoFocus
                />
                <p className="text-sm text-gray-400 mt-2">
                    Se creará en: <code className="bg-gray-100 px-1.5 py-0.5 rounded">{currentPath || 'repositorio'}</code>
                </p>
            </Modal.Body>
            <Modal.Footer>
                <div className="flex items-center justify-end gap-2 w-full">
                    <button
                        onClick={() => { setShowNewFolderModal(false); setNewFolderName(''); }}
                        className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition-colors text-sm font-medium"
                    >
                        Cancelar
                    </button>
                    <button
                        onClick={handleCreateFolder}
                        disabled={!newFolderName.trim()}
                        className="px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg transition-colors text-sm font-medium disabled:opacity-50"
                    >
                        Crear
                    </button>
                </div>
            </Modal.Footer>
        </Modal>
    );

    const renderDeleteConfirm = () => (
        <Modal
            isOpen={!!deleteConfirm}
            onClose={() => setDeleteConfirm(null)}
            size="sm"
            closeOnOverlay={true}
            closeOnEscape={true}
        >
            <Modal.Header onClose={() => setDeleteConfirm(null)}>
                <div className="flex items-center gap-3">
                    <Trash2 size={20} className="text-red-500" />
                    <span>Confirmar eliminación</span>
                </div>
            </Modal.Header>
            <Modal.Body>
                <p className="text-gray-700">
                    ¿Eliminar {deleteConfirm?.tipo === 'carpeta' ? 'la carpeta' : 'el archivo'} <strong>{deleteConfirm?.nombre}</strong>?
                </p>
                {deleteConfirm?.tipo === 'carpeta' && (
                    <p className="text-sm text-gray-400 mt-2">Solo se pueden eliminar carpetas vacías.</p>
                )}
            </Modal.Body>
            <Modal.Footer>
                <div className="flex items-center justify-end gap-2 w-full">
                    <button
                        onClick={() => setDeleteConfirm(null)}
                        className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition-colors text-sm font-medium"
                    >
                        Cancelar
                    </button>
                    <button
                        onClick={() => handleDelete(deleteConfirm.nombre, deleteConfirm.tipo)}
                        className="px-4 py-2 bg-red-500 hover:bg-red-600 text-white rounded-lg transition-colors text-sm font-medium"
                    >
                        Eliminar
                    </button>
                </div>
            </Modal.Footer>
        </Modal>
    );

    // ─── Render principal ───────────────────────────────────────────────

    return (
        <div className="max-w-[1600px] mx-auto animate-fadeIn pb-8">
            {/* Header */}
            <header className="mb-6 pt-4">
                <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-4">
                        {(view === 'browse' || view === 'search' || view === 'favoritos') && (
                            <motion.button
                                onClick={() => {
                                    setView('categorias');
                                    setGlobalSearch('');
                                    setSearchResults([]);
                                }}
                                className="p-2.5 bg-gray-100 hover:bg-gray-200 rounded-xl transition-colors"
                                whileHover={{ scale: 1.05 }}
                                whileTap={{ scale: 0.95 }}
                            >
                                <ArrowLeft size={20} className="text-gray-600" />
                            </motion.button>
                        )}
                        <h2 className="page-title">Documentos</h2>
                    </div>

                    <div className="flex items-center gap-2">
                        {/* Botón favoritos */}
                        <motion.button
                            onClick={() => {
                                if (view === 'favoritos') {
                                    setView('categorias');
                                } else {
                                    setView('favoritos');
                                    fetchFavoritos();
                                }
                            }}
                            className={`p-2.5 rounded-xl transition-colors ${view === 'favoritos' ? 'bg-yellow-100 text-yellow-600' : 'bg-gray-100 hover:bg-gray-200 text-gray-600'}`}
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                            title="Mis favoritos"
                        >
                            <Star size={20} fill={view === 'favoritos' ? 'currentColor' : 'none'} />
                        </motion.button>

                        <motion.button
                            onClick={() => {
                                if (view === 'categorias') { fetchCategorias(); fetchStats(); }
                                else if (view === 'browse') browsePath(currentPath);
                                else if (view === 'favoritos') fetchFavoritos();
                            }}
                            disabled={loading}
                            className="p-2.5 bg-gray-100 hover:bg-gray-200 rounded-xl transition-colors disabled:opacity-50"
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                        >
                            <RefreshCw size={20} className={`text-gray-600 ${loading ? 'animate-spin' : ''}`} />
                        </motion.button>
                    </div>
                </div>

                {/* Barra de búsqueda global y controles */}
                {view !== 'favoritos' && (
                    <div className="flex flex-col md:flex-row gap-3">
                        <div className="relative flex-1">
                            {aiSearchMode ? (
                                <Sparkles className="absolute left-4 top-1/2 -translate-y-1/2 text-purple-500" size={20} />
                            ) : (
                                <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
                            )}
                            <input
                                type="text"
                                placeholder={aiSearchMode ? "Describe lo que necesitas..." : "Buscar en documentos..."}
                                className={`w-full pl-12 pr-24 py-3 rounded-xl border focus:outline-none focus:ring-2 transition-all bg-white ${aiSearchMode ? 'border-purple-200 focus:ring-purple-500/20 focus:border-purple-500' : 'border-gray-200 focus:ring-blue-500/20 focus:border-blue-500'}`}
                                value={globalSearch}
                                onChange={(e) => setGlobalSearch(e.target.value)}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter' && aiSearchMode && globalSearch.length >= 3) {
                                        handleAiSearch(globalSearch);
                                    }
                                }}
                            />
                            <div className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center gap-1">
                                {globalSearch && (
                                    <button
                                        onClick={() => {
                                            setGlobalSearch('');
                                            setSearchResults([]);
                                            setAiSearchResults([]);
                                            if (view === 'search') setView('categorias');
                                        }}
                                        className="p-1 hover:bg-gray-100 rounded-lg transition-colors"
                                    >
                                        <X size={16} className="text-gray-400" />
                                    </button>
                                )}
                                {/* Toggle IA */}
                                <motion.button
                                    onClick={() => setAiSearchMode(!aiSearchMode)}
                                    className={`p-1.5 rounded-lg transition-colors ${aiSearchMode ? 'bg-purple-100 text-purple-600' : 'hover:bg-gray-100 text-gray-400'}`}
                                    whileHover={{ scale: 1.1 }}
                                    whileTap={{ scale: 0.9 }}
                                    title={aiSearchMode ? 'Búsqueda IA activa — Enter para buscar' : 'Activar búsqueda inteligente'}
                                >
                                    <Sparkles size={16} />
                                </motion.button>
                                {/* Botón buscar IA */}
                                {aiSearchMode && globalSearch.length >= 3 && (
                                    <motion.button
                                        onClick={() => handleAiSearch(globalSearch)}
                                        disabled={aiSearchLoading}
                                        className="p-1.5 bg-purple-500 text-white rounded-lg hover:bg-purple-600 disabled:opacity-50"
                                        initial={{ scale: 0 }}
                                        animate={{ scale: 1 }}
                                        whileHover={{ scale: 1.05 }}
                                    >
                                        {aiSearchLoading ? <Loader2 size={16} className="animate-spin" /> : <Search size={16} />}
                                    </motion.button>
                                )}
                            </div>
                        </div>

                        {view === 'browse' && (
                            <div className="flex items-center gap-2">
                                <div className="flex bg-gray-100 rounded-xl p-1">
                                    <button
                                        onClick={() => setDisplayMode('grid')}
                                        className={`p-2 rounded-lg transition-colors ${displayMode === 'grid' ? 'bg-white shadow-sm text-gray-900' : 'text-gray-500 hover:text-gray-700'}`}
                                        title="Vista de cuadrícula"
                                    >
                                        <Grid3X3 size={18} />
                                    </button>
                                    <button
                                        onClick={() => setDisplayMode('list')}
                                        className={`p-2 rounded-lg transition-colors ${displayMode === 'list' ? 'bg-white shadow-sm text-gray-900' : 'text-gray-500 hover:text-gray-700'}`}
                                        title="Vista de lista"
                                    >
                                        <List size={18} />
                                    </button>
                                </div>

                                <div className="relative group">
                                    <button className="flex items-center gap-2 px-3 py-2 bg-gray-100 hover:bg-gray-200 rounded-xl transition-colors text-sm font-medium text-gray-700">
                                        <ArrowUpDown size={16} />
                                        <span className="hidden sm:inline">Ordenar</span>
                                    </button>
                                    <div className="absolute right-0 top-full mt-1 bg-white rounded-xl shadow-lg border border-gray-100 py-1 min-w-[160px] opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-20">
                                        {[
                                            { key: 'nombre', label: 'Nombre' },
                                            { key: 'fecha', label: 'Fecha' },
                                            { key: 'tamaño', label: 'Tamaño' },
                                            { key: 'tipo', label: 'Tipo' }
                                        ].map(opt => (
                                            <button
                                                key={opt.key}
                                                onClick={() => toggleSort(opt.key)}
                                                className={`w-full px-4 py-2 text-left text-sm hover:bg-gray-50 flex items-center justify-between ${sortBy === opt.key ? 'text-blue-600 font-medium' : 'text-gray-700'}`}
                                            >
                                                {opt.label}
                                                {sortBy === opt.key && (
                                                    <span className="text-xs">{sortOrder === 'asc' ? '↑' : '↓'}</span>
                                                )}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </header>

            {/* Loading state */}
            {loading && (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    {[1, 2, 3].map(i => (
                        <div key={i} className="h-64 bg-gray-100 rounded-3xl skeleton" />
                    ))}
                </div>
            )}

            {/* Content */}
            {!loading && (
                <AnimatePresence mode="wait">
                    {view === 'categorias' ? (
                        <motion.div
                            key="categorias"
                            initial={{ opacity: 0, x: -20 }}
                            animate={{ opacity: 1, x: 0 }}
                            exit={{ opacity: 0, x: 20 }}
                        >
                            {renderCategorias()}
                        </motion.div>
                    ) : view === 'search' ? (
                        <motion.div
                            key="search"
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -20 }}
                        >
                            {renderSearch()}
                        </motion.div>
                    ) : view === 'favoritos' ? (
                        <motion.div
                            key="favoritos"
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -20 }}
                        >
                            {renderFavoritos()}
                        </motion.div>
                    ) : (
                        <motion.div
                            key="browser"
                            initial={{ opacity: 0, x: 20 }}
                            animate={{ opacity: 1, x: 0 }}
                            exit={{ opacity: 0, x: -20 }}
                        >
                            {renderBrowser()}
                        </motion.div>
                    )}
                </AnimatePresence>
            )}

            {/* Modals */}
            {renderPreviewModal()}
            {renderNewFolderModal()}
            {renderDeleteConfirm()}
        </div>
    );
};

export default DocumentsView;
